/**
 * Update Blog Post Mutation Hook
 *
 * Update existing blog posts with automatic cache invalidation.
 * Computes word_count and reading_time automatically.
 *
 * @example
 * const { mutate, isPending } = useUpdateBlogPost();
 * mutate({ id: 'abc123', data: formData });
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateBlogPost } from '../api';
import { blogPostsKeys } from './useBlogPosts';
import type { BlogPost, BlogPostFormData } from '../types';
import { stripHtml, calculateWordCount, calculateReadingTime } from '../utils/contentStats';

interface UpdateBlogPostParams {
  id: string;
  data: BlogPostFormData;
}

/**
 * Hook to update an existing blog post
 * Automatically computes word_count and reading_time from content
 *
 * @returns Mutation object with mutate function and state
 */
export const useUpdateBlogPost = () => {
  const queryClient = useQueryClient();

  return useMutation<BlogPost, Error, UpdateBlogPostParams>({
    mutationFn: async ({ id, data }: UpdateBlogPostParams) => {
      const plainText = stripHtml(data.content);
      const wordCount = calculateWordCount(plainText);

      const blogData: Partial<BlogPost> = {
        ...data,
        word_count: wordCount,
        reading_time: calculateReadingTime(wordCount),
      };

      return updateBlogPost(id, blogData);
    },
    onSuccess: (result) => {
      // Invalidate both the list and the specific post
      void queryClient.invalidateQueries({ queryKey: blogPostsKeys.all });
      void queryClient.invalidateQueries({ queryKey: blogPostsKeys.detail(result.id) });
    },
    onError: (error) => {
      console.error('Error updating blog post:', error);
    },
  });
};
